import type {
  APIShareRegionalTrendResponse,
  APISharedProfileResponse,
  TechName,
} from '@see-stella/types'
import { useCallback, useEffect, useState } from 'react'
import { BrandMark } from '../../components/BrandMark'
import {
  closeOwnerView,
  isHostedInApp,
  requestOwnerToken,
} from '../../lib/ownerToken'
import {
  authedPreviewMine,
  authedPreviewRegionalTrend,
  ShareApiError,
} from '../../lib/shareApi'
import { ProfileNotice } from '../share/ProfileNotice'
import { ReportView } from '../share/ProfileScreen'
import '../share/ProfileScreen.css'
import { OwnerBar } from './OwnerBar'
import { ShareSheet } from './ShareSheet'
import { useOwnerShare } from './useOwnerShare'

type Load =
  | { status: 'loading' }
  | { status: 'no-app' }
  | { status: 'ready'; profile: APISharedProfileResponse }
  | { status: 'error'; reason: ShareApiError['reason'] }

/**
 * Owner mode (/owner). The Stella app hosts this in a WebView; the page asks the
 * host for the owner's Firebase ID token (handshake) and renders the owner's OWN
 * full profile via the authed `previewMine`, reusing the recipient `ReportView` so
 * the two are identical by construction. No share controls yet — the control bar
 * and Create/Manage steps come in a later slice. Opened directly in a browser (no
 * app host), it shows a neutral "open from the app" state.
 */
export function OwnerPage() {
  const [load, setLoad] = useState<Load>({ status: 'loading' })
  const [sheetOpen, setSheetOpen] = useState(false)
  // Only hydrate the existing link when the app is hosting us — a plain-browser hit
  // can never answer the token handshake (mirrors the load effect's guard below).
  const share = useOwnerShare(requestOwnerToken, isHostedInApp())
  // Stable handlers so the sheet's Escape-key effect doesn't re-bind each render.
  const openSheet = useCallback(() => setSheetOpen(true), [])
  const closeSheet = useCallback(() => setSheetOpen(false), [])

  useEffect(() => {
    // A plain browser hit (no app host) can never get a token: show the neutral
    // "open from the app" state immediately rather than waiting on a handshake that
    // will never be answered.
    if (!isHostedInApp()) {
      setLoad({ status: 'no-app' })
      return
    }
    let live = true
    requestOwnerToken()
      .then(async (token) => {
        if (!live) return
        if (!token) {
          // Hosted but no token: this route is signed-in-only, so a null here is a
          // transient token-mint failure (or an 8s handshake timeout), NOT a plain
          // browser hit. Show a retryable error, not the "open from the app" state.
          setLoad({ status: 'error', reason: 'unknown' })
          return
        }
        const profile = await authedPreviewMine(token)
        if (live) setLoad({ status: 'ready', profile })
      })
      .catch((err) => {
        if (!live) return
        const reason = err instanceof ShareApiError ? err.reason : 'unknown'
        setLoad({ status: 'error', reason })
      })
    return () => {
      live = false
    }
  }, [])

  // Region trends run over a FRESHLY-requested token per call (the handshake mints a
  // current one each time), so a long owner session never hits an expired token.
  // A null token (signed out mid-session) makes the Authorization header be omitted
  // → backend 401 → useRegionalTrend swallows it → simply no region dots. Injected
  // into ReportView so owner and recipient share the identical render path.
  const fetchRegionalTrend = useCallback(
    async (techName: TechName): Promise<APIShareRegionalTrendResponse> => {
      const token = await requestOwnerToken()
      return authedPreviewRegionalTrend(token ?? '', techName)
    },
    []
  )

  if (load.status === 'no-app') {
    return (
      <main className='profile profile--center'>
        <div className='profile__notice'>
          <BrandMark />
          <h1 className='profile__notice-title'>
            Open this from the Stella app
          </h1>
          <p className='profile__notice-sub'>
            Your skin profile opens inside the Stella app, where you choose what
            to share and get a link.
          </p>
        </div>
      </main>
    )
  }

  if (load.status === 'loading') {
    return (
      <main className='profile profile--center'>
        <div className='profile__spinner' role='status'>
          <span className='visually-hidden'>Loading your profile</span>
        </div>
      </main>
    )
  }

  if (load.status === 'error') {
    return <ProfileNotice reason={load.reason} />
  }

  return (
    <>
      <OwnerBar
        includes={share.includes}
        onToggle={share.setInclude}
        onBack={closeOwnerView}
        onShare={openSheet}
        busy={share.hydrating || share.creating || share.stopping}
      />
      {/* The FULL previewMine payload, gated LIVE by the control bar's includes —
          identical to the recipient's render path. */}
      <ReportView
        profile={load.profile}
        fetchRegionalTrend={fetchRegionalTrend}
        includes={share.includes}
      />
      {sheetOpen && (
        <ShareSheet
          includes={share.includes}
          link={share.link}
          creating={share.creating}
          stopping={share.stopping}
          error={share.error}
          onCreate={async (expiry) => {
            // create() never rejects; it resolves false + sets share.error on
            // failure, so the sheet stays open showing the error. On success the
            // link is now live: keep the sheet open so it switches to Manage
            // (the OS share sheet has already been invoked).
            await share.create(expiry)
          }}
          onShare={share.share}
          onStop={share.revoke}
          onClose={closeSheet}
        />
      )}
    </>
  )
}
